import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

export interface AutoReconnectCloseEvent {
  code: number;
  reason: string;
  wasClean: boolean;
}

export interface UseTranscriptionAutoReconnectOptions {
  /** 再接続対象の meetingId。null のときは何もしない。 */
  meetingId: string | null;
  /** 実際の再接続関数。`resume: true` で再接続される。 */
  connect: (params: { meetingId: string; resume: true }) => Promise<string>;
  /** 再接続ハンドリングを有効にするか。`state.value === 'recording'` のときだけ true にする想定。 */
  enabled: boolean;
  /** 最大試行回数。default 5。 */
  maxAttempts?: number;
  /** 初回遅延 (ms)。default 500。 */
  initialDelayMs?: number;
  /** バックオフ倍率。default 2。 */
  backoffFactor?: number;
  /** バックオフ上限 (ms)。default 8000。 */
  maxDelayMs?: number;
  /** 試行開始通知（attempt は 1 始まり） */
  onReconnecting?: (attempt: number) => void;
  /** 再接続成功通知 */
  onReconnected?: () => void;
  /** 全試行失敗時の通知 */
  onGiveUp?: (error: Error) => void;
}

export interface UseTranscriptionAutoReconnectReturn {
  /** `onClose` event を受けたときに呼ぶ。intentional な close は skip される。 */
  handleClose: (event: AutoReconnectCloseEvent) => void;
  /** 再接続試行中フラグ */
  isReconnecting: boolean;
  /** 現在の試行回数 */
  attempts: number;
}

/**
 * WebSocket 切断時に exponential backoff で自動再接続を試みるフック。
 * AWS API Gateway WebSocket の 2 時間上限による code=1001 切断を吸収するのが主目的。
 */
export function useTranscriptionAutoReconnect(
  options: UseTranscriptionAutoReconnectOptions,
): UseTranscriptionAutoReconnectReturn {
  const {
    meetingId,
    connect,
    enabled,
    maxAttempts = 5,
    initialDelayMs = 500,
    backoffFactor = 2,
    maxDelayMs = 8000,
    onReconnecting,
    onReconnected,
    onGiveUp,
  } = options;

  const [isReconnecting, setIsReconnecting] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const attemptsRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const cancelledRef = useRef(false);

  const meetingIdRef = useRef(meetingId);
  const connectRef = useRef(connect);
  const enabledRef = useRef(enabled);
  const onReconnectingRef = useRef(onReconnecting);
  const onReconnectedRef = useRef(onReconnected);
  const onGiveUpRef = useRef(onGiveUp);

  useEffect(() => {
    meetingIdRef.current = meetingId;
    connectRef.current = connect;
    enabledRef.current = enabled;
    onReconnectingRef.current = onReconnecting;
    onReconnectedRef.current = onReconnected;
    onGiveUpRef.current = onGiveUp;
  });

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    attemptsRef.current = 0;
    setAttempts(0);
    setIsReconnecting(false);
    inFlightRef.current = false;
    clearPendingTimeout();
  }, [clearPendingTimeout]);

  const tryReconnect = useCallback(() => {
    if (timeoutRef.current !== null) return;
    if (cancelledRef.current) return;
    if (!enabledRef.current) {
      resetState();
      return;
    }
    const id = meetingIdRef.current;
    if (id === null) {
      resetState();
      return;
    }
    if (attemptsRef.current >= maxAttempts) {
      const err = new Error(
        `Auto-reconnect failed after ${maxAttempts} attempts`,
      );
      logger.error('[useTranscriptionAutoReconnect] Give up', { attempts: attemptsRef.current });
      setIsReconnecting(false);
      inFlightRef.current = false;
      onGiveUpRef.current?.(err);
      return;
    }

    const attempt = attemptsRef.current + 1;
    attemptsRef.current = attempt;
    setAttempts(attempt);
    const delay = Math.min(
      initialDelayMs * backoffFactor ** (attempt - 1),
      maxDelayMs,
    );

    logger.info('[useTranscriptionAutoReconnect] Scheduling attempt', {
      attempt,
      delayMs: delay,
    });
    onReconnectingRef.current?.(attempt);

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      if (cancelledRef.current || !enabledRef.current) {
        resetState();
        return;
      }
      const currentId = meetingIdRef.current;
      if (currentId === null) {
        resetState();
        return;
      }
      inFlightRef.current = true;
      connectRef
        .current({ meetingId: currentId, resume: true })
        .then(() => {
          if (cancelledRef.current) return;
          logger.info('[useTranscriptionAutoReconnect] Reconnected', { attempt });
          attemptsRef.current = 0;
          setAttempts(0);
          setIsReconnecting(false);
          inFlightRef.current = false;
          onReconnectedRef.current?.();
        })
        .catch((err) => {
          if (cancelledRef.current) return;
          logger.warn('[useTranscriptionAutoReconnect] Attempt failed', {
            attempt,
            error: err instanceof Error ? err.message : String(err),
          });
          inFlightRef.current = false;
          tryReconnect();
        });
    }, delay);
  }, [backoffFactor, initialDelayMs, maxAttempts, maxDelayMs, resetState]);

  const handleClose = useCallback(
    (event: AutoReconnectCloseEvent) => {
      if (!enabledRef.current) return;
      if (meetingIdRef.current === null) return;
      // 正常 close（ユーザー操作 / unmount 経由）は再接続しない
      if (event.code === 1000 && event.wasClean) return;
      if (inFlightRef.current) return;

      logger.info('[useTranscriptionAutoReconnect] Triggered by close', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      attemptsRef.current = 0;
      setAttempts(0);
      setIsReconnecting(true);
      cancelledRef.current = false;
      tryReconnect();
    },
    [tryReconnect],
  );

  // enabled=false へ切り替わったら進行中の再接続を打ち切る
  useEffect(() => {
    if (!enabled) {
      cancelledRef.current = true;
      clearPendingTimeout();
      setIsReconnecting(false);
      attemptsRef.current = 0;
      setAttempts(0);
      inFlightRef.current = false;
    } else {
      cancelledRef.current = false;
    }
  }, [enabled, clearPendingTimeout]);

  // unmount 時にタイマー破棄
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearPendingTimeout();
    };
  }, [clearPendingTimeout]);

  return { handleClose, isReconnecting, attempts };
}
